import { PureComponent } from 'react'

import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import grey from '@material-ui/core/colors/grey'
import { withStyles } from '@material-ui/core/styles'
import InfoIcon from '@material-ui/icons/Info'
import Cookie from 'js-cookie'
import PropTypes from 'prop-types'

import { getPath } from '../../oakra/components/base-app'
import { post } from '../../tools/request'
import { copyObject } from '../../tools/utils'
import { getChannelShippingInfoManager } from './channel-shipping-info-managers'

const styles = (theme) => ({
    dialog: {},
    initialLoadingProgress: {
        alignSelf: 'center',
    },
    dialogContent: {
        minHeight: 100,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    dialogActions: {
        paddingRight: 24,
        paddingLeft: 24,
        paddingBottom: 15,
        marginTop: 0,
    },
    ordersToInclude: {
        marginTop: 30,
    },
    maxOrdersMessage: {
        marginTop: 10,
    },
    shippingInitFieldsProgress: {
        alignSelf: 'center',
        margin: '0 auto',
    },
    shippingInfoTitle: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 15,
    },
    shippingInfoTitleText: {
        color: grey[500],
        marginLeft: 5,
    },
    shopFields: {
        marginBottom: 20,
    },
    channelIcon: {
        width: 75,
        alignSelf: 'center',
    },
    pickPackBatch: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    submitProgress: {
        marginRight: 10,
    },
    initShippingInfoError: {
        color: theme.palette.error.main,
        fontSize: '0.8rem',
        lineHeight: 1.2,
    },
    pickPackBatchList: {
        marginLeft: 12,
        marginTop: 6,
        lineHeight: '1.6rem',
        '& a': {
            color: 'inherit',
            '&:hover': {
                color: theme.palette.secondary.main,
            },
        },
    },
    pickPackBatchListItem: {
        listStyleType: 'none',
    },
})

const manualShippingShop = {
    id: 0,
    channel: { name: 'Other' },
    shop_name: gettext('Manual / Webstore / Delivery by seller orders'),
}

class ShippingInfoManager extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            initShippingInfo: {},
            initShippingInfoFields: null,
            initShippingInfoError: '',
            initShippingInfoResult: {},
            fieldInfo: {},
            loading: false,
            submitted: false,
        }

        this.reset = this.reset.bind(this)
        this.fetchInitShippingInfoFields = this.fetchInitShippingInfoFields.bind(this)
        this.handleInitShippingInfoChange = this.handleInitShippingInfoChange.bind(this)
        this.handleSubmit = this.handleSubmit.bind(this)
    }

    componentDidMount() {
        if (this.props.salesFilter) {
            this.setState({ loading: true }, this.fetchInitShippingInfoFields)
        }
    }

    componentDidUpdate() {
        if (!this.state.initShippingInfoFields && !this.state.loading && this.props.salesFilter) {
            this.setState({ loading: true }, this.fetchInitShippingInfoFields)
        }
    }

    reset() {
        this.setState({
            initShippingInfo: {},
            initShippingInfoFields: null,
            fieldInfo: {},
            loading: false,
        })
    }

    fetchInitShippingInfoFields() {
        const { initShippingInfoFieldsAPIURL, salesFilter } = this.props

        fetch(initShippingInfoFieldsAPIURL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                'X-CSRFToken': Cookie.get('csrftoken'),
            },
            body: JSON.stringify({ sales_filter: salesFilter }),
        })
            .then((response) => response.json())
            .then((data) => {
                const fieldInfo = copyObject(this.state.fieldInfo)
                const initShippingInfo = copyObject(this.state.initShippingInfo)

                data.forEach((shopFields) => {
                    if (shopFields.field_info) {
                        const shopPK = shopFields.shop?.id || 0
                        initShippingInfo[shopPK] = {}

                        fieldInfo[shopPK] = shopFields.field_info
                    }
                })

                this.setState({
                    initShippingInfoFields: data,
                    initShippingInfo,
                    fieldInfo,
                    loading: false,
                })
            })
    }

    handleInitShippingInfoChange(shopID) {
        return (initShippingInfoForShop) => {
            this.setState((state) => {
                const initShippingInfo = copyObject(state.initShippingInfo)
                initShippingInfo[shopID] = initShippingInfoForShop
                return { initShippingInfo }
            })
        }
    }

    getPayload(initShippingInfo) {
        // Turn value objs on initShippingInfo into actual values
        const { salesFilter } = this.props

        const payload = { sales_filter: salesFilter }
        const initShippingInfoToSubmit = copyObject(initShippingInfo)

        Object.entries(initShippingInfoToSubmit).forEach(([shopPK, initShippingInfoForShop]) => {
            Object.entries(initShippingInfoForShop).forEach(([field, valueObj]) => {
                if (field === 'pickup_time_id_by_shipping_provider') {
                    const valueField = this.state.fieldInfo[shopPK][field]['value_field']
                    initShippingInfoToSubmit[shopPK][field] = Object.keys(valueObj).reduce(
                        (result, shippingProviderId) => {
                            result[shippingProviderId] = valueObj[shippingProviderId][valueField]
                            return result
                        },
                        {}
                    )
                } else if (typeof valueObj === 'object') {
                    const valueField = this.state.fieldInfo[shopPK][field]['value_field']
                    initShippingInfoToSubmit[shopPK][field] = valueObj[valueField]
                } else {
                    initShippingInfoToSubmit[shopPK][field] = valueObj
                }
            })
        })

        payload['init_shipping_info'] = initShippingInfoToSubmit

        return payload
    }

    handleSubmit() {
        const { initShippingInfoAPIURL } = this.props

        window.analytics.track('Confirm order shipping', {
            page: window.location.href,
            feature: 'order',
            mode: 'pc',
            trigger: 'button click',
        })

        const postData = this.getPayload(this.state.initShippingInfo)

        this.setState({ loading: true }, () => {
            post(initShippingInfoAPIURL, postData).then(({ ok, ...response }) => {
                if (!ok || response?.error_message) {
                    this.setState({
                        loading: false,
                        submitted: true,
                        initShippingInfoError:
                            response?.error_message ||
                            gettext(
                                'There was a problem submiting shipping info for orders. Please try again.'
                            ),
                    })
                } else {
                    this.setState({
                        loading: false,
                        submitted: true,
                        initShippingInfoResult: response,
                    })
                }
            })
        })
    }

    closeDialog = () => {
        this.reset()
        this.props.handleCloseShippingInfoManager(this.state.submitted)
    }

    render() {
        const { classes } = this.props
        const { initShippingInfoResult, initShippingInfoError } = this.state
        let screen = 'loading'
        let initShippingInfoFields = []
        if (this.state.initShippingInfoFields) {
            screen = 'init'
            this.state.initShippingInfoFields.forEach((item) => {
                if (item.field_info) {
                    initShippingInfoFields.push(item)
                }
            })
        }
        if (initShippingInfoResult?.pick_pack_batch_ids) {
            screen = 'submitted'
        }

        return (
            <Dialog
                className={classes.dialog}
                open={this.props.open}
                onClose={this.closeDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{gettext('Confirm shipping')}</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {screen === 'init' && (
                        <div>
                            {initShippingInfoFields.map((shopFields) => {
                                const shop = shopFields.shop || manualShippingShop
                                const InitShippingInfo = getChannelShippingInfoManager(
                                    shop.channel.name
                                )

                                return (
                                    <div key={shop.id} className={classes.shopFields}>
                                        <div className={classes.shippingInfoTitle}>
                                            {shop.channel?.channel_icon_url && (
                                                <img
                                                    className={classes.channelIcon}
                                                    src={shop.channel.channel_icon_url}
                                                />
                                            )}
                                            <Typography className={classes.shippingInfoTitleText}>
                                                {shop.shop_name}
                                            </Typography>
                                        </div>
                                        <InitShippingInfo
                                            key={shop.id}
                                            initShippingInfoFields={shopFields.init_shipping_info}
                                            initShippingInfo={this.state.initShippingInfo[shop.id]}
                                            fieldInfo={this.state.fieldInfo[shop.id]}
                                            handleInfoChange={this.handleInitShippingInfoChange(
                                                shop.id
                                            )}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {screen === 'submitted' && (
                        <div style={{ marginBottom: 24 }}>
                            <Typography variant="body2" style={{ display: 'flex' }}>
                                <InfoIcon style={{ color: grey[500] }} />{' '}
                                <span style={{ marginLeft: 8 }}>
                                    {gettext(
                                        'The system will now initiate shipping for all selected orders.'
                                    )}
                                </span>
                            </Typography>
                            <Typography variant="body1" style={{ marginTop: 12, marginLeft: 32 }}>
                                {gettext('Go to batches')}
                            </Typography>
                            <ul className={classes.pickPackBatchList}>
                                {initShippingInfoResult.pick_pack_batch_ids.map((ppbId) => (
                                    <li className={classes.pickPackBatchListItem}>
                                        <a
                                            href={getPath('pickPackBatch').replace(
                                                'pick_pack_batch_id',
                                                ppbId
                                            )}
                                            target="_blank"
                                        >
                                            {gettext('Batch')} {ppbId}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {screen === 'loading' && (
                        <CircularProgress
                            className={classes.shippingInitFieldsProgress}
                            color="secondary"
                            size={30}
                        />
                    )}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    {screen === 'init' && (
                        <>
                            {initShippingInfoError && (
                                <div>
                                    <Typography className={classes.initShippingInfoError}>
                                        {initShippingInfoError}
                                    </Typography>
                                </div>
                            )}

                            <div>
                                {this.state.loading && (
                                    <CircularProgress
                                        className={classes.submitProgress}
                                        color="secondary"
                                        size={20}
                                    />
                                )}
                                {!!initShippingInfoError ? (
                                    <Button color="secondary" onClick={this.closeDialog}>
                                        {gettext('Close')}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        disabled={
                                            !this.state.initShippingInfoFields || this.state.loading
                                        }
                                        className={classes.shippingInfoConfirmButton}
                                        onClick={this.handleSubmit}
                                    >
                                        {gettext('Confirm')}
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                    {screen === 'submitted' && (
                        <div>
                            <Button color="secondary" onClick={this.closeDialog}>
                                {gettext('Close')}
                            </Button>
                        </div>
                    )}
                </DialogActions>
            </Dialog>
        )
    }
}

ShippingInfoManager.propTypes = {
    open: PropTypes.bool.isRequired,
    handleCloseShippingInfoManager: PropTypes.func.isRequired,
    salesFilter: PropTypes.object.isRequired,
}

export default withStyles(styles)(ShippingInfoManager)
